import { useCallback, useEffect, useRef, useState } from 'react'
import {
  isPageElement,
  isTopLevelFrameElement,
  SketchElement,
  useGoToLayer,
} from '../../../../../inspector'
import {
  findSketchElementFromLayerPathHash,
  useCanvasClickHandler,
  useCanvasMouseMoveHandler,
} from '../sketchSceneLib'
import { isUserInteractionEnabledOnSketchElement } from './isUserInteractionEnabledOnSketchElement'
import { useQueryParams } from '@sketch/modules-common'

/**
 * Create event handler and react states to display hover and selected elements state.
 */
export function useInspectorOverlayEvents(
  sketchSceneRootElement: SketchElement | null,
  /** This should only be enabled when the inspector is open and ready */
  isEnabled: boolean,
  /**
   * @see useInitializeSelectedElement
   */
  autoSelectRootInitially: boolean,
  isTreeFullyLoaded: boolean
) {
  const [selectedElement, setSelectedElement] = useState<SketchElement | null>(
    null
  )
  useInitializeSelectedElement(
    setSelectedElement,
    sketchSceneRootElement,
    autoSelectRootInitially && !selectedElement,
    isTreeFullyLoaded
  )

  const [hoveredElement, setHoveredElement] = useState<SketchElement | null>(
    null
  )

  const guardedSetSelectedElement = useCallback(
    (selectedElement: SketchElement | null) => {
      if (selectedElement && isPageElement(selectedElement)) {
        setSelectedElement(null)
        // We don't allow to select the page element
        return
      }
      setSelectedElement(selectedElement)
    },
    [setSelectedElement]
  )

  const guardedSetHoveredElement = useCallback(
    (hoveredElement: SketchElement | null) => {
      setHoveredElement(prevHoveredElement => {
        if (hoveredElement?.elementUUID === prevHoveredElement?.elementUUID) {
          // Nothing to update, avoids unnecessary re-renders
          return prevHoveredElement
        }

        if (hoveredElement && isPageElement(hoveredElement)) {
          // We don't allow to hover the page element
          return null
        }

        return hoveredElement
      })
    },
    []
  )

  const { clickHandler, mouseDownHandle } = useCanvasClickHandler(
    isEnabled,
    sketchSceneRootElement,
    guardedSetSelectedElement,
    isUserInteractionEnabledOnSketchElement
  )

  const mouseMoveHandler = useCanvasMouseMoveHandler(
    isEnabled,
    sketchSceneRootElement,
    guardedSetHoveredElement,
    isUserInteractionEnabledOnSketchElement
  )

  return {
    hoveredElement,
    selectedElement,
    /**
     * We need to also be able to set the selected element identifier from outside of events.
     * (using context menu for examples)
     */
    setSelectedElement: guardedSetSelectedElement,
    setHoveredElement: guardedSetHoveredElement,
    clickHandler,
    mouseMoveHandler,
    mouseDownHandle,
  }
}

/**
 * There are two initialization behaviors to handle:
 * - Auto-select the root frame element when in frame view mode
 * - Initialize from URL query param if there is a layer path hash specified
 * The second one has priority over the first one if both conditions are met.
 *
 * Note: On page load, we want to initialize the selected element from the URL query param.
 * However, we don't necessarily have the full sketch scene tree at that time (especially when there are fragments).
 * Therefore we need to retry to initialize the selected element each time the layers tree is updated.
 */
export function useInitializeSelectedElement(
  setSelectedElement: React.Dispatch<
    React.SetStateAction<SketchElement | null>
  >,
  sketchSceneRootElement: SketchElement | null,
  /**
   * Automatically select the top-level frame element when the inspector is active.
   * This can only work if the root is a top-level frame element (frame view).
   */
  autoSelectRootFrame: boolean,
  isTreeFullyLoaded: boolean
) {
  /**
   * Tracks if we have already do the initialization from URL (should happen only once).
   */
  const hasInitializedRef = useRef(false)
  const queryParams = useQueryParams<'FRAME' | 'SHARE_PAGE_CANVAS_VIEW'>()
  const layerPathHashFromUrl = queryParams.layerPath || null
  const goToLayer = useGoToLayer()

  /**
   * In the frame view we want to select the root frame element by default.
   * Note: If there is a layerPathHash in the URL we don't auto select.
   */
  useEffect(() => {
    if (
      !autoSelectRootFrame ||
      !sketchSceneRootElement ||
      !isTopLevelFrameElement(sketchSceneRootElement) ||
      layerPathHashFromUrl // If there is a layerPathHash in the URL, we don't auto select
    ) {
      return
    }

    setSelectedElement(sketchSceneRootElement)
  }, [
    autoSelectRootFrame,
    layerPathHashFromUrl,
    setSelectedElement,
    sketchSceneRootElement,
  ])

  /**
   * Initialize selected element from URL
   */
  useEffect(() => {
    // There is nothing to initialize to
    if (!layerPathHashFromUrl) {
      hasInitializedRef.current = true
      return
    }

    // It has already initialized.
    if (hasInitializedRef.current) {
      return
    }

    // Initialize the selected element from the URL param
    const sketchElement = findSketchElementFromLayerPathHash(
      sketchSceneRootElement,
      layerPathHashFromUrl
    )

    if (sketchElement) {
      setSelectedElement(sketchElement)
      goToLayer(sketchElement)
      hasInitializedRef.current = true
      return
    }

    // Else the element is not found but could be loaded later through a frame fragment.
    if (isTreeFullyLoaded) {
      // All fragments have loaded and element still not found - invalid layerPathHash in URL, nothing to select, ignore
      hasInitializedRef.current = true
    }
    // otherwise we wait for fragments to load and try again later
  }, [
    isTreeFullyLoaded,
    hasInitializedRef,
    layerPathHashFromUrl,
    setSelectedElement,
    sketchSceneRootElement,
    goToLayer,
  ])
}
